import * as T from 'three';
import { seeded } from './world-data';

export const breeze = { value: 0 };

// World-space variation keeps detail continuous across merged village geometry.
export function weathered(material: T.MeshStandardMaterial, scale = 3, strength = 0.16) {
  material.onBeforeCompile = shader => {
    shader.vertexShader = shader.vertexShader.replace('#include <common>', '#include <common>\nvarying vec3 vSurface;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvSurface = position;');
    shader.fragmentShader = shader.fragmentShader.replace('#include <common>', `#include <common>
      varying vec3 vSurface;
      float grain(vec3 p) { return fract(sin(dot(p,vec3(127.1,311.7,74.7)))*43758.5453); }
      float noise3(vec3 p) {
        vec3 i=floor(p), f=fract(p); f=f*f*(3.0-2.0*f);
        return mix(mix(mix(grain(i),grain(i+vec3(1,0,0)),f.x),mix(grain(i+vec3(0,1,0)),grain(i+vec3(1,1,0)),f.x),f.y),
        mix(mix(grain(i+vec3(0,0,1)),grain(i+vec3(1,0,1)),f.x),mix(grain(i+vec3(0,1,1)),grain(i+vec3(1,1,1)),f.x),f.y),f.z);
      }`).replace('#include <color_fragment>', `#include <color_fragment>
      float pat = noise3(vSurface * ${scale.toFixed(2)}) * 0.65 + noise3(vSurface * ${(scale * 4).toFixed(2)}) * 0.35;
      diffuseColor.rgb *= 1.0 + (pat - 0.5) * ${strength.toFixed(2)};
      `);
  };
  material.customProgramCacheKey = () => `weathered-${scale}-${strength}`;
  return material;
}

export function windMaterial(color: string, amount: number) {
  const material = new T.MeshStandardMaterial({color, side:T.DoubleSide, roughness:1});
  material.onBeforeCompile = shader => {
    shader.uniforms.uBreeze = breeze;
    shader.vertexShader = shader.vertexShader.replace('#include <common>', '#include <common>\nuniform float uBreeze;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        vec3 anchor = instanceMatrix[3].xyz;
        float gust = sin(uBreeze * 1.6 + anchor.x * 0.25 + anchor.z * 0.16);
        transformed.x += gust * ${amount.toFixed(3)} * max(position.y, 0.0);
        transformed.z += cos(uBreeze + anchor.x * 0.18) * ${amount.toFixed(3)} * 0.4 * max(position.y, 0.0);
      `);
  };
  material.customProgramCacheKey = () => `wind-${amount}`;
  return material;
}

/** Bend an existing plant or tree material in the breeze. Uses the instance position, so it belongs on InstancedMesh materials. */
export function sway(material: T.Material, amount: number) {
  const m = material as T.MeshStandardMaterial;
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uBreeze = breeze;
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nuniform float uBreeze;\n")
      .replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>
        vec3 anchor = instanceMatrix[3].xyz;
        float gust = sin(uBreeze * 1.6 + anchor.x * 0.25 + anchor.z * 0.16);
        transformed.x += gust * ${amount.toFixed(3)} * max(position.y, 0.0);
        transformed.z += cos(uBreeze + anchor.x * 0.18) * ${amount.toFixed(3)} * 0.4 * max(position.y, 0.0);
      `,
      );
  };
  m.customProgramCacheKey = () => `sway-${amount.toFixed(3)}`;
  return m;
}

// Each canopy is a cloud of individual curved leaves, with soft crown normals.
// sizeScale > 1 makes fewer, larger leaves for distant (cheaper) trees.
export function leafCloud(count: number, sizeScale = 1) {
  const rand = seeded(9281), positions:number[]=[], normals:number[]=[], colors:number[]=[];
  for(let i=0;i<count;i++) {
    const a=rand()*Math.PI*2, y=rand()*1.8-0.9, radius=Math.sqrt(1-y*y)*(0.65+rand()*0.35);
    const center=new T.Vector3(Math.cos(a)*radius,y,Math.sin(a)*radius);
    const normal=center.clone().normalize(); normal.y=Math.max(0.35,normal.y); normal.normalize();
    const tangent=new T.Vector3(Math.cos(a+1.57),0,Math.sin(a+1.57));
    const up=new T.Vector3().crossVectors(normal,tangent).normalize();
    const size=(0.17+rand()*0.15)*sizeScale;
    const corners=[center.clone().addScaledVector(up,size*1.3),center.clone().addScaledVector(tangent,size*0.65),center.clone().addScaledVector(up,-size),center.clone().addScaledVector(tangent,-size*0.65)];
    const tint=new T.Color().setHSL(0.22+rand()*0.045,0.45,0.39+rand()*0.22);
    for(const k of [0,1,2,0,2,3]) {
      positions.push(...corners[k].toArray()); normals.push(...normal.toArray()); colors.push(tint.r,tint.g,tint.b);
    }
  }
  const g=new T.BufferGeometry();
  g.setAttribute('position',new T.Float32BufferAttribute(positions,3));
  g.setAttribute('normal',new T.Float32BufferAttribute(normals,3));
  g.setAttribute('color',new T.Float32BufferAttribute(colors,3));
  return g;
}

export function addSky(scene:T.Scene) {
  const sky=new T.Mesh(new T.SphereGeometry(450,32,16),new T.ShaderMaterial({
    side:T.BackSide, depthWrite:false,
    vertexShader:`varying vec3 vDirection; void main(){vDirection=position;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
    fragmentShader:`varying vec3 vDirection;
      float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
      float noise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.0-2.0*f);return mix(mix(hash(i),hash(i+vec2(1,0)),f.x),mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),f.x),f.y);}
      void main(){vec3 d=normalize(vDirection);float h=max(d.y,0.0);
        vec3 color=mix(vec3(0.73,0.86,0.85),vec3(0.12,0.43,0.68),pow(h,0.55));
        vec2 p=d.xz/max(d.y+0.15,0.08)*2.5;
        float n=noise(p)*0.57+noise(p*2.1)*0.28+noise(p*4.3)*0.15;
        float clouds=smoothstep(0.51,0.73,n)*smoothstep(0.02,0.18,h);
        color=mix(color,vec3(0.99,0.97,0.88),clouds*0.88);
        float sun=pow(max(dot(d,normalize(vec3(-50,85,35))),0.0),180.0);
        color+=vec3(0.6,0.42,0.2)*sun;
        gl_FragColor=vec4(color,1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`
  }));
  sky.frustumCulled=false;sky.renderOrder=-10;scene.add(sky);
}

export function mountainRidge(seed:number, radius:number, height:number) {
  const rand=seeded(seed), segments=100, rows=10, positions:number[]=[], indices:number[]=[], colors:number[]=[];
  const peaks=Array.from({length:segments},()=>0.45+rand()*0.55);
  for(let row=0;row<=rows;row++) for(let i=0;i<=segments;i++) {
    const a=i/segments*Math.PI*2, t=row/rows;
    const peak=peaks[i%segments]*0.5+peaks[(i+1)%segments]*0.3+peaks[(i+segments-1)%segments]*0.2;
    const radial=radius+(t-0.5)*85;
    const ridge=Math.pow(Math.sin(t*Math.PI),1.2)*height*peak;
    positions.push(Math.cos(a)*radial,ridge-9+Math.sin(a*19)*ridge*0.12,-65+Math.sin(a)*radial);
    const c=new T.Color('#779baf').lerp(new T.Color('#b1c9cf'),t*0.5+peak*0.2);
    c.multiplyScalar(0.82+rand()*0.22);colors.push(c.r,c.g,c.b);
    if(row&&i){const k=row*(segments+1)+i;indices.push(k,k-1,k-segments-1,k-1,k-segments-2,k-segments-1);}
  }
  const g=new T.BufferGeometry();g.setAttribute('position',new T.Float32BufferAttribute(positions,3));g.setAttribute('color',new T.Float32BufferAttribute(colors,3));g.setIndex(indices);g.computeVertexNormals();
  return new T.Mesh(g,new T.MeshBasicMaterial({vertexColors:true,side:T.DoubleSide}));
}
